import { useCallback, useEffect, useState } from 'react';
import { getUserData, getLocalIdToken, getUserMemberType } from 'helpers/padi/cognito';
import { fetchWithIdToken } from 'helpers/utils/fetch';
import { useAccount } from 'frontastic';

const getUsersAvatar = async (idToken: string, affiliateId: string, getProjectConfig: Function) => {
  try {
    const proApiDomain = await getProjectConfig('EXTENSION_PADI_APIS_PRO_API_DOMAIN');
    if (proApiDomain.isError || !proApiDomain.setting) return;
    const response: Response = await fetch(`${proApiDomain.setting}/certifications/photos/getphoto`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({ affiliateId: affiliateId }),
      method: 'post',
    });
    const blobContent = await response.blob();
    if (blobContent?.size === 0) {
      return null;
    }
    return URL.createObjectURL(blobContent);
  } catch (e) {
    return null;
  }
};

const getUsersClubEntitlement = async (idToken: string, getProjectConfig: (key: string) => Promise<any>) => {
  if (!idToken) return;
  try {
    const piccoloApiDomain = await getProjectConfig('EXTENSION_PADI_APIS_PICCOLO_API_DOMAIN');
    if (piccoloApiDomain.isError || !piccoloApiDomain.setting) return;
    const response = await fetchWithIdToken(`${piccoloApiDomain.setting}/customer/entitlements/active`);
    const result = await response.json();
    if (result) return result;
    return null;
  } catch (e) {
    console.error('----- e: ', e);
    return null;
  }
};

const getUsersEntitlement = async (sub: string, getProjectConfig: (key: string) => Promise<any>) => {
  if (!sub) return;
  try {
    const piccoloApiDomain = await getProjectConfig('EXTENSION_PADI_APIS_PICCOLO_API_DOMAIN');
    if (piccoloApiDomain.isError) return;
    const response: Response = await fetch(`${piccoloApiDomain.setting}/offer/recommendation?sub=${sub}`, {
      method: 'get',
    });

    const result = await response.json();
    if (result) return result;
    return null;
  } catch (e) {
    console.error('----- e: ', e);
    return null;
  }
};

const useGetUserInfo = () => {
  const { getProjectConfig, account } = useAccount();
  const [avatar, setAvatar] = useState<any>();
  const [idToken, setIdToken] = useState<string>();
  const [userData, setUserData] = useState<{ [key: string]: any } | undefined>(undefined);
  const [memberType, setMemberType] = useState<string | undefined>(undefined);
  const [clubEntitlements, setClubEntitlements] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Cache for entitlements by sub
  const [entitlementsCache, setEntitlementsCache] = useState<Map<string, any[]>>(new Map());

  const getUserClubEntitlments = useCallback(async () => {
    if (clubEntitlements.length > 0) return clubEntitlements; // Return cached data

    let token = idToken;
    if (!token) {
      token = getLocalIdToken();
      setIdToken(token);
    }
    if (!token || !getProjectConfig) return [];

    const response = await getUsersClubEntitlement(token, getProjectConfig);
    if (response) {
      setClubEntitlements(response);
      return response;
    }
    return [];
  }, [getProjectConfig, idToken, clubEntitlements]);

  const getUserEntitlments = useCallback(
    async (sub: string) => {
      // Check cache first
      if (entitlementsCache.has(sub)) {
        return entitlementsCache.get(sub)!;
      }

      if (!getProjectConfig) return [];
      const response = await getUsersEntitlement(sub, getProjectConfig);
      if (response) {
        setEntitlementsCache((prev) => new Map(prev.set(sub, response)));
        return response;
      }
      return [];
    },
    [getProjectConfig, entitlementsCache],
  );

  // Clear all data when account changes (logout)
  useEffect(() => {
    if (!account?.accountId) {
      setAvatar(undefined);
      setIdToken(undefined);
      setUserData(undefined);
      setMemberType(undefined);
      setClubEntitlements([]);
      setEntitlementsCache(new Map());
      setIsLoading(false);
    }
  }, [account?.accountId]);

  useEffect(() => {
    if (isLoading || userData || !account?.accountId) return; // Prevent multiple calls

    setIsLoading(true);
    const idToken = getLocalIdToken();
    if (idToken) setIdToken(idToken);
    const cognitoUserData = getUserData();
    const affiliateId = cognitoUserData?.['custom:affiliate_id'];
    const affiliateTypeId = cognitoUserData?.['custom:affiliate_type_id'];

    const fetchAvatar = async (idToken: string, affiliateId: string) => {
      const userAvatar = await getUsersAvatar(idToken, affiliateId, getProjectConfig);
      setAvatar(userAvatar);
    };

    const getMemberType = getUserMemberType(affiliateTypeId);
    if (getMemberType) setMemberType(getMemberType);

    if (!idToken || !affiliateId) {
      setIsLoading(false);
      return;
    }

    setUserData(cognitoUserData);
    fetchAvatar(idToken, affiliateId).finally(() => setIsLoading(false));
  }, [getProjectConfig, account?.accountId, isLoading, userData]);

  return {
    avatar,
    idToken,
    userData,
    getUserClubEntitlments,
    getUserEntitlments,
    memberType,
    isLoading,
  };
};

export default useGetUserInfo;
